import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useOutletContext } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { trainingApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";

/**
 * The layout route around every /training page. Today, History, a
 * session's detail and Settings all need the person's settings (units,
 * equipment steps, bands, bodyweight, time zone) and the exercise catalog,
 * so this loads both once and hands them down through the outlet context,
 * alongside the shell's own (`me`, `meLoading`, `forYouCount`):
 *
 *   const { me, settings, exercises, reloadSettings, applySettings,
 *           reloadExercises } = useOutletContext() || {};
 *
 * - `reloadSettings()` / `reloadExercises()` refetch quietly — the page
 *   stays on screen — and resolve to the fresh value (or reject: the caller
 *   says what failed).
 * - `applySettings(settings)` takes a value the server already confirmed
 *   (a PATCH response) without a refetch.
 * Newest wins: a reload that started before a later one (or before an
 * apply) never lands over it.
 *
 * No humans-only gate: agents may use Training (spec Assumption 1).
 */
function TrainingLayout() {
  const shell = useOutletContext() || {};
  const [settings, setSettings] = useState(null);
  const [exercises, setExercises] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  const loadGen = useRef(0);
  const settingsGen = useRef(0);
  const exercisesGen = useRef(0);

  const load = useCallback(async () => {
    const id = ++loadGen.current;
    const forSettings = ++settingsGen.current;
    const forExercises = ++exercisesGen.current;
    setLoading(true);
    setError(null);
    try {
      const [settingsResponse, exercisesResponse] = await Promise.all([
        trainingApi.settings.get(),
        trainingApi.exercises.list(),
      ]);
      if (!mounted.current || id !== loadGen.current) return;
      // Newest wins against a reload or an apply. No test can fail on these
      // two checks (c9 Y1): load runs only while nothing is ready — the
      // first load and the error's Retry — and until then the Outlet isn't
      // rendered, so no page can reload or apply in between. Kept so a later
      // caller of load (a refresh while the pages are up) can't land stale.
      if (forSettings === settingsGen.current) setSettings(settingsResponse.data);
      if (forExercises === exercisesGen.current) setExercises(exercisesResponse.data);
    } catch (err) {
      if (!mounted.current || id !== loadGen.current) return;
      setError(apiErrorMessage(err, "Something went wrong."));
    } finally {
      if (mounted.current && id === loadGen.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
    };
  }, [load]);

  const reloadSettings = useCallback(async () => {
    const id = ++settingsGen.current;
    const { data } = await trainingApi.settings.get();
    if (mounted.current && id === settingsGen.current) setSettings(data);
    return data;
  }, []);

  const applySettings = useCallback((next) => {
    settingsGen.current += 1;
    setSettings(next);
  }, []);

  const reloadExercises = useCallback(async () => {
    const id = ++exercisesGen.current;
    const { data } = await trainingApi.exercises.list();
    if (mounted.current && id === exercisesGen.current) setExercises(data);
    return data;
  }, []);

  const ready = settings !== null && exercises !== null;
  let content;
  if (!ready && loading) {
    content = (
      <div className="page-container mt-6 pt-4">
        <div className="py-12 text-center" role="status">
          <Loader2 className="inline size-6 animate-spin text-muted-foreground" aria-hidden="true" />
          <span className="sr-only">Loading your training…</span>
        </div>
      </div>
    );
  } else if (!ready) {
    content = (
      <div className="page-container mt-6 pt-4">
        <Alert variant="destructive">
          <AlertDescription className="flex flex-wrap items-center gap-3">
            <span className="flex-1">
              Couldn&apos;t load your training settings and exercises: {error}
            </span>
            <Button size="sm" variant="outline" className="h-11 md:h-8" onClick={load}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  } else {
    content = (
      <Outlet
        context={{
          ...shell,
          settings,
          exercises,
          reloadSettings,
          applySettings,
          reloadExercises,
        }}
      />
    );
  }
  // [data-training]: inside it the light theme's muted text is a step
  // darker, so page subtitles and the pager read AA on the page background
  // (tailwind.css; a dialog portalled out of it carries the attribute too).
  return <div data-training="">{content}</div>;
}

export default TrainingLayout;
